import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS, fetchWithTimeout } from '../config/api';

interface User {
  id: string;
  user_id: string;
  user_first_name: string;
  user_last_name: string;
  user_email: string;
  user_phone: string;
  token: string;
  user_type: string;
  role: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

const TOKEN_KEY = 'auth_token';
const USER_KEY = 'user_data';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(() => {
    const savedUser = localStorage.getItem(USER_KEY);
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const login = async (email: string, password: string) => {
    setError(null);
    setIsLoading(true);

    try {
      const formData = new URLSearchParams();
      formData.append('user_email', email);
      formData.append('user_password', password);

      const response = await fetchWithTimeout(API_ENDPOINTS.LOGIN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        mode: 'cors',
        body: formData
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        const userData = data.data;
        // Store token and user data in localStorage
        localStorage.setItem(TOKEN_KEY, userData.token);
        localStorage.setItem(USER_KEY, JSON.stringify(userData));
        setUser(userData);
        navigate('/');
      } else {
        const errorMessage = data.message || 'Invalid email or password';
        setError(errorMessage);
      }
    } catch (err) {
      const error = err as Error;
      console.error('Login failed:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      
      if (error.message === 'Request timeout') {
        setError('Connection timed out. Please try again.');
      } else if (error.message.includes('Failed to fetch')) {
        setError('Unable to connect to the server. Please check your internet connection.');
      } else {
        setError('An error occurred. Please try again later.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    // Clear localStorage
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
    navigate('/login');
  };

  // Add authorization header to all API requests
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      // Update the fetchWithTimeout default headers
      const originalFetch = window.fetch;
      window.fetch = function (...args) {
        const [resource, initialConfig] = args;
        let config = initialConfig;
        
        // Only add the header for our API endpoints
        if (typeof resource === 'string' && resource.includes(API_ENDPOINTS.BASE_URL)) {
          config = {
            ...config,
            headers: {
              ...config?.headers,
              'Authorization': `Bearer ${token}`
            }
          };
        }
        
        return originalFetch(resource, config);
      };

      // Cleanup function to restore original fetch
      return () => {
        window.fetch = originalFetch;
      };
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, isLoading, error, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};